// What's new (docs/plans/whats-new.md; canvas row "What's new in the app"): what each update
// brings, the viewer's own kind of business first. The words live in `../whats-new.ts`.
import { Badge, Card, IconTile, PageHeader, buttonClass, cx } from '@ims/ui'
import {
  BarChart3,
  Bell,
  CalendarCheck,
  Check,
  ChevronDown,
  ChevronRight,
  Home,
  Mail,
  Monitor,
  Package,
  Receipt,
  Sparkles,
  Truck,
  UserRound,
  X,
} from '@ims/ui/icons'
import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useBusinessMode, useSettings } from '../hooks.ts'
import { dateLabel } from '../lib/format.ts'
import {
  AUDIENCE_WORD,
  audienceOf,
  cardRelease,
  entriesFor,
  kindWords,
  markSeen,
  pickedCount,
  readSeen,
  releasedFor,
  subscribeSeen,
  type Viewer,
} from '../lib/whats-new.ts'
import { useSession, useUser } from '../session.tsx'
import { useRole, useWorkspace } from '../workspace.tsx'
import {
  RELEASES,
  type WhatsNewArea,
  type WhatsNewEntry,
  type WhatsNewIcon,
  type WhatsNewRelease,
} from '../whats-new.ts'

const ICONS: Record<WhatsNewIcon, typeof Mail> = {
  screen: Monitor,
  ticket: Receipt,
  chart: BarChart3,
  mail: Mail,
  home: Home,
  truck: Truck,
  box: Package,
  calendar: CalendarCheck,
  bell: Bell,
  check: Check,
}

// Spelled out so Tailwind sees every class.
const WASH: Record<WhatsNewArea, string> = {
  sell: 'from-sell-wash',
  people: 'from-people-wash',
  stock: 'from-stock-wash',
  buy: 'from-buy-wash',
  insight: 'from-insight-wash',
  kitchen: 'from-kitchen-wash',
  neutral: 'from-neutral-wash',
  take: 'from-take-wash',
}

/** Everything What's new needs about this person, the releases they may read and what is seen. */
export function useWhatsNew() {
  const user = useUser()
  const { platform } = useSession()
  const { restaurant, businessType } = useBusinessMode()
  const role = useRole()
  const { memberships } = useWorkspace()
  const seen = useSyncExternalStore(subscribeSeen, () => readSeen(user.id))
  const viewer: Viewer = { restaurant, businessType, role, businesses: memberships.length }
  const releases = releasedFor(RELEASES, platform.apiUrl)
  const newest = releases[0] ?? null
  const newCount = newest && newest.id !== seen ? pickedCount(entriesFor(newest, viewer)) : 0
  const card = cardRelease(releases, seen, viewer, new Date().toISOString().slice(0, 10))
  return {
    viewer,
    releases,
    newest,
    seen,
    newCount,
    card,
    markSeen: (id: string) => markSeen(user.id, id),
  }
}

function Who({ role }: { role: WhatsNewEntry['minRole'] }) {
  const words: Record<WhatsNewEntry['minRole'], string> = {
    viewer: 'Everyone',
    waiter: 'Everyone',
    staff: 'Staff and up',
    manager: 'Managers and up',
    admin: 'Admins and owners',
    owner: 'Owners',
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-soft px-2 py-0.5 text-xs font-medium text-muted">
      <UserRound className="size-3" aria-hidden />
      {words[role]}
    </span>
  )
}

function ShowMe({ entry }: { entry: WhatsNewEntry }) {
  if (!entry.show) return null
  return (
    <Link
      to={entry.show.to}
      className="inline-flex min-h-9 items-center gap-1 text-sm font-semibold text-primary hover:underline"
    >
      {entry.show.label}
      <ChevronRight className="size-4" aria-hidden />
    </Link>
  )
}

function EntryIcon({ entry, size }: { entry: WhatsNewEntry; size: 'md' | 'lg' }) {
  const Icon = ICONS[entry.icon]
  return (
    <IconTile area={entry.area} size={size}>
      <Icon className={size === 'lg' ? 'size-5' : 'size-[18px]'} aria-hidden />
    </IconTile>
  )
}

// Small drawings in tokens, never screenshots: they suit both themes and cost nothing to load.
function Preview({ id }: { id: string }): ReactNode {
  if (id === 'kitchen-counter') {
    const tickets: [string, string, string, [number, string][]][] = [
      [
        '41',
        'T4',
        'border-t-warning',
        [
          [2, 'Momo, buff'],
          [1, 'Thukpa'],
        ],
      ],
      ['42', 'Takeaway 7', 'border-t-info', [[3, 'Chowmein']]],
      [
        '43',
        'T9',
        'border-t-success',
        [
          [1, 'Thakali set'],
          [2, 'Lassi'],
        ],
      ],
    ]
    return (
      <div
        aria-hidden
        className="flex gap-2 rounded-xl border border-kitchen-light bg-kitchen-wash p-3.5"
      >
        {tickets.map(([no, where, tone, lines]) => (
          <div
            key={no}
            className={cx(
              'min-w-0 flex-1 rounded-lg border-t-4 bg-surface p-2 text-xs shadow-card',
              tone,
            )}
          >
            <div className="mb-1 flex flex-wrap justify-between gap-x-1.5 font-bold">
              <span>#{no}</span>
              <span className="text-muted">{where}</span>
            </div>
            {lines.map(([q, dish]) => (
              <div key={dish} className="flex gap-1.5">
                <b className="tabular">{q}</b>
                <span>{dish}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }
  if (id === 'receive-bill' || id === 'receive-ingredients') {
    const rows: [string, string, string, boolean][] =
      id === 'receive-bill'
        ? [
            ['Wai Wai noodles, carton', '10', '620.00', true],
            ['Sunsilk 180 ml', '24', '1,850.00', false],
          ]
        : [
            ['Buff mince, kg', '10', '540.00', true],
            ['Cooking oil, 15 L tin', '2', '6,900.00', false],
          ]
    return (
      <div aria-hidden className="rounded-xl border border-buy-light bg-buy-wash px-3.5 py-3">
        <div className="mb-0.5 text-xs text-muted">Bill 2231 · Bhatbhateni Suppliers</div>
        {rows.map(([name, qty, rate, free]) => (
          <div
            key={name}
            className="grid grid-cols-[1fr_auto_auto] items-center gap-2.5 border-b border-border py-1.5 text-[13px]"
          >
            <span className="min-w-0">
              {name}
              {free && (
                <span className="ml-1.5 whitespace-nowrap rounded-full bg-success-light px-1.5 py-px text-[11px] font-bold text-success">
                  +2 free
                </span>
              )}
            </span>
            <span className="tabular text-muted">{qty}</span>
            <span className="tabular min-w-16 text-right">{rate}</span>
          </div>
        ))}
        <div className="mt-2 rounded-lg bg-warning-light px-2.5 py-1.5 text-xs font-semibold text-warning">
          {id === 'receive-bill'
            ? 'Rs 1,850 a carton is 3 times the last one. Right?'
            : 'Rs 6,900 a tin is 3 times the last one. Right?'}
        </div>
      </div>
    )
  }
  return null
}

function Lead({ entry }: { entry: WhatsNewEntry }) {
  const preview = <Preview id={entry.id} />
  return (
    <Card
      className={cx(
        'grid items-center gap-5 bg-linear-170 to-surface to-55% lg:grid-cols-2',
        WASH[entry.area],
      )}
    >
      <div className="flex flex-col gap-3.5">
        <div className="flex items-start gap-3">
          <EntryIcon entry={entry} size="lg" />
          <div className="min-w-0">
            <h3 className="text-base font-bold lg:text-lg">{entry.title}</h3>
            <p className="mt-1 text-sm text-muted lg:text-base">{entry.line}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <Who role={entry.minRole} />
          <ShowMe entry={entry} />
        </div>
      </div>
      {preview}
    </Card>
  )
}

function Item({ entry }: { entry: WhatsNewEntry }) {
  return (
    <Card className="flex flex-col gap-2.5 p-4">
      <div className="flex items-start gap-3">
        <EntryIcon entry={entry} size="md" />
        <div className="min-w-0">
          <h3 className="font-bold">{entry.title}</h3>
          <p className="mt-0.5 text-sm text-muted">{entry.line}</p>
        </div>
      </div>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-2.5 lg:pl-12">
        <Who role={entry.minRole} />
        <ShowMe entry={entry} />
      </div>
    </Card>
  )
}

/** Three columns, or two when three would leave one card alone on the last row. */
const gridOf = (n: number) =>
  cx('grid gap-3', n % 3 === 1 && n > 1 ? 'lg:grid-cols-2' : 'lg:grid-cols-3')

function Section({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3" aria-label={title}>
      <div>
        <h2 className="font-bold">{title}</h2>
        <p className="text-sm text-subtle">{note}</p>
      </div>
      {children}
    </section>
  )
}

function ReleaseBody({
  release,
  viewer,
  businessName,
}: {
  release: WhatsNewRelease
  viewer: Viewer
  businessName: string
}) {
  const { mine, everyone, fixed } = entriesFor(release, viewer)
  const word = AUDIENCE_WORD[audienceOf(viewer)]
  const [lead, ...rest] = mine
  return (
    <div className="flex flex-col gap-6">
      {lead && (
        <Section title={`For your ${word}`} note={`Made for ${word}s like ${businessName}.`}>
          <Lead entry={lead} />
          {rest.length > 0 && (
            <div className={gridOf(rest.length)}>
              {rest.map((e) => (
                <Item key={e.id} entry={e} />
              ))}
            </div>
          )}
        </Section>
      )}
      {everyone.length > 0 && (
        <Section title="For every business" note="Shops, restaurants and workshops all get these.">
          <div className={gridOf(everyone.length)}>
            {everyone.map((e) => (
              <Item key={e.id} entry={e} />
            ))}
          </div>
        </Section>
      )}
      {fixed.length > 0 && (
        <Section
          title="Fixed"
          note={
            fixed.some((f) => f.when === 'several-businesses')
              ? 'Shown to you because you have more than one business.'
              : 'Things that went wrong before this update.'
          }
        >
          <Card className="px-4 py-1">
            <ul>
              {fixed.map((f) => (
                <li key={f.id} className="flex items-start gap-2.5 py-2.5">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                  <span>{f.text}</span>
                </li>
              ))}
            </ul>
          </Card>
        </Section>
      )}
    </div>
  )
}

function releaseDate(release: WhatsNewRelease, settings: ReturnType<typeof useSettings>) {
  return release.date ? dateLabel(release.date, settings) : 'Coming in the next update'
}

export function WhatsNewScreen() {
  const { viewer, releases, newest, seen } = useWhatsNew()
  const user = useUser()
  const settings = useSettings()
  const { current } = useWorkspace()
  const businessName = current?.tenant.name ?? 'your business'
  // Read before this visit marks it seen, so "New" stays on the page while it is open.
  const [wasNew] = useState(() => !!newest && newest.id !== seen)
  const [open, setOpen] = useState<string | null>(null)
  const newestId = newest?.id
  useEffect(() => {
    if (newestId) markSeen(user.id, newestId)
  }, [user.id, newestId])
  const [first, ...older] = releases

  return (
    <div className="max-w-6xl">
      <PageHeader title="What’s new" subtitle="What changed in Sano Godam, newest first." />
      {!first ? (
        <p className="text-muted">Nothing here yet.</p>
      ) : (
        <div className="flex flex-col gap-6">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="text-xl font-bold">{first.title}</h2>
              {wasNew && <Badge tone="primary">New</Badge>}
            </div>
            <p className="text-sm text-muted">{releaseDate(first, settings)}</p>
          </div>
          <p className="flex items-center gap-2.5 rounded-xl bg-soft px-3.5 py-2.5 text-sm text-muted">
            <Sparkles className="size-4 shrink-0" aria-hidden />
            <span>
              Showing what’s new for <b className="text-ink">{businessName}</b>, a{' '}
              {kindWords(viewer)}, as {/^[aeiou]/.test(viewer.role) ? 'an' : 'a'} {viewer.role}.
            </span>
          </p>
          <ReleaseBody release={first} viewer={viewer} businessName={businessName} />
          {older.length > 0 && (
            <Section title="Earlier updates" note="Everything from before, by update.">
              <Card className="overflow-hidden p-0">
                {older.map((r, i) => (
                  <div key={r.id} className={cx(i > 0 && 'border-t border-border')}>
                    <button
                      type="button"
                      aria-expanded={open === r.id}
                      onClick={() => setOpen(open === r.id ? null : r.id)}
                      className="flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-soft"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold">{r.title}</span>
                        <span className="block text-xs text-subtle">
                          {releaseDate(r, settings)}
                        </span>
                      </span>
                      <span className="text-sm text-muted">
                        {pickedCount(entriesFor(r, viewer))} things
                      </span>
                      <ChevronDown
                        className={cx('size-4 transition-transform', open === r.id && 'rotate-180')}
                        aria-hidden
                      />
                    </button>
                    {open === r.id && (
                      <div className="border-t border-border bg-background p-4">
                        <ReleaseBody release={r} viewer={viewer} businessName={businessName} />
                      </div>
                    )}
                  </div>
                ))}
              </Card>
            </Section>
          )}
        </div>
      )}
    </div>
  )
}

/** The dashboard's quiet card after an update; never a pop-up (board WhatsNewDash). */
export function WhatsNewCard() {
  const { card, viewer, markSeen: mark } = useWhatsNew()
  if (!card) return null
  const { mine, everyone } = entriesFor(card, viewer)
  const word = AUDIENCE_WORD[audienceOf(viewer)]
  const titles = [...mine, ...everyone].map((e) => e.title)
  const heading =
    mine.length > 0
      ? `New in this update: ${mine.length} thing${mine.length === 1 ? '' : 's'} for your ${word}`
      : `New in this update: ${titles.length} thing${titles.length === 1 ? '' : 's'}`
  const shown = titles.slice(0, 2)
  const lower = (t: string) => t.charAt(0).toLowerCase() + t.slice(1)
  return (
    <Card className="mb-4 flex flex-wrap items-center gap-3.5 bg-linear-170 from-primary-light to-surface to-60% p-4">
      <IconTile area="sell" size="md">
        <Sparkles className="size-[18px]" aria-hidden />
      </IconTile>
      <div className="min-w-0 flex-1 basis-64">
        <h2 className="font-bold">{heading}</h2>
        <p className="text-sm text-muted">
          {shown[0]}
          {shown[1] ? `, ${lower(shown[1])}` : ''}
          {titles.length > 2 ? ', and more.' : '.'}
        </p>
      </div>
      <Link to="/whats-new" className={buttonClass({ variant: 'secondary', size: 'sm' })}>
        See what’s new
      </Link>
      <button
        type="button"
        aria-label="Dismiss what’s new"
        onClick={() => mark(card.id)}
        className="flex size-9 items-center justify-center rounded-lg text-subtle hover:bg-soft hover:text-ink"
      >
        <X className="size-4" aria-hidden />
      </button>
    </Card>
  )
}
